import {
  ATTACK_SPEED_JEWEL_ID,
  BLADE_CUT_JEWEL_ID,
  type JewelSocketProfile,
  toExactBigInt,
} from '@kenocyber/shared';
import { BPS_ONE } from './exact';
import type { DamageOrigin, JewelEmittedEffect } from './origin';
import { damageOriginMayTrigger, damageOriginMayTriggerJewel } from './origin';

/**
 * **A loteria de proc: uma rolagem por disparo, e no máximo um proc.**
 *
 * ## Porque uma rolagem e não uma por joia
 *
 * Porque uma nave equipa seis sockets. Seis sorteios independentes davam 46 % de
 * probabilidade de dois ou mais procs no mesmo acerto, e um pico de 325 % do
 * dano-base num só quadro. Uma rolagem sobre intervalos disjuntos torna dois
 * procs simultâneos **impossíveis por construção**, e não improváveis por
 * afinação.
 *
 * ## Porque a chance nunca é dividida
 *
 * `chance = maxChanceBps × min(levelSum, 30) / 30` é a lei, e `13/30` não tem
 * representação decimal finita - `30 = 2 × 3 × 5` e o três nunca desaparece.
 * Dividir antes de comparar transformaria 0,433333 % em 0,43 %.
 *
 * Então não se divide: a rolagem vive em `[0, BPS_ONE × PROC_LEVEL_CAP)` e é
 * comparada com `maxChanceBps × min(levelSum, cap)`. Aritmética inteira exata,
 * e o denominador é DERIVADO - mover o teto não parte nada.
 *
 * ## O que este ficheiro não faz
 *
 * Não sorteia. Recebe a rolagem já feita, porque quem a faz é a simulação com o
 * seu fluxo autoritativo, e uma função que sorteia não pode ser testada com um
 * valor escolhido. Não aplica dano, não conhece um alvo, não conhece um
 * projétil e não conhece nada visual.
 */

/** O nível a que a chance deixa de crescer. Decisão do gate V28G.F. */
export const PROC_LEVEL_CAP = 30;

/**
 * O espaço da rolagem. **Derivado**, nunca escrito à mão.
 *
 * `BPS_ONE × PROC_LEVEL_CAP`. Escrever 300 000 aqui seria um número que deixa
 * de estar certo no dia em que o teto mudar, e que ninguém iria procurar.
 */
export const PROC_CHANCE_DENOMINATOR = Number(BPS_ONE) * PROC_LEVEL_CAP;

/** Os procs que uma joia pode originar num disparo de canhão. */
export type OutgoingJewelProcId = Extract<JewelEmittedEffect, 'cyclone_cut'>;

/**
 * Um id que o REGISTO reconhece, e a única forma de perguntar.
 *
 * Existe para que um decodificador de instantânea possa validar o campo de um
 * projétil sem escrever um literal: o registo é o sítio onde a identidade de um
 * proc vive, e um `=== 'cyclone_cut'` noutro ficheiro seria uma segunda lista a
 * envelhecer em silêncio.
 */
export function isOutgoingProcId(value: string): value is OutgoingJewelProcId {
  return OUTGOING_PROC_REGISTRY.some((definition) => definition.procId === value);
}

/**
 * A seleção congelada que um projétil carrega.
 *
 * `entropy` dá determinismo a decisões internas futuras - uma direção, um
 * subtipo - sem uma segunda rolagem no impacto, que seria uma rolagem que o
 * snapshot teria de preservar separadamente. Um inteiro sem sinal de 32 bits,
 * nunca um objeto aberto nem uma string de payload.
 *
 * Nunca carrega identificador de conta, skin, socket ou instância: o projétil
 * atravessa o fio para seis clientes.
 */
export interface JewelProcSelection {
  readonly id: OutgoingJewelProcId;
  readonly entropy: number;
  /**
   * **O dano adicional exato, em milli, decidido quando o projétil NASCEU.**
   *
   * ## Porque o resultado, e não os ingredientes
   *
   * Porque guardar `levelSum` e recalcular no impacto é guardar uma receita, e
   * uma receita é avaliada com os ingredientes que existirem quando for lida.
   * Comprar um ponto de dano com o tiro no ar mudaria o corte de um projétil já
   * disparado - o jogador veria um tiro mudar de ideias em voo, que é
   * exatamente o que `damageMilli` deixou de fazer no gate V28F.B.
   *
   * Este campo é a mesma fronteira temporal, aplicada ao proc. Nada aqui é
   * reavaliado: nem o nível, nem as cópias, nem a joia continuar encaixada.
   *
   * ## Porque `bigint`
   *
   * Porque deriva do dano-base autoritativo, que não tem tecto de jogo - a
   * contagem de Corações Primordiais vive em `numeric(40, 0)`. Atravessa as
   * instantâneas como STRING decimal, pela mesma regra de todo o resto.
   */
  readonly cutMilli: bigint;
}

/**
 * A seleção como um DOCUMENTO a guarda.
 *
 * Igual à seleção viva num campo só, e pela razão de sempre: `cutMilli` é
 * `bigint`, uma instantânea é JSON, e JSON não tem `bigint`. Atravessa como
 * string decimal, exatamente como um saldo e como `damageMilli`.
 *
 * Um tipo próprio em vez de reutilizar {@link JewelProcSelection}: partilhar o
 * tipo faria `JSON.stringify` atirar em produção em vez de o compilador recusar
 * na altura de escrever.
 */
export interface JewelProcSelectionWire {
  readonly id: OutgoingJewelProcId;
  readonly entropy: number;
  readonly cutMilli: string;
}

/** Do vivo para o documento. Uma função, para os dois modos não divergirem. */
export function jewelProcToWire(
  selection: JewelProcSelection | null,
): JewelProcSelectionWire | null {
  return selection === null
    ? null
    : { id: selection.id, entropy: selection.entropy, cutMilli: selection.cutMilli.toString(10) };
}

/**
 * Do documento para o vivo, e **sem reavaliar coisa nenhuma**.
 *
 * A magnitude vem do documento porque é a única fonte que ainda diz o que ela
 * era: o perfil da corrida pode ter mudado desde o disparo. Recalculá-la aqui
 * seria repor exatamente o defeito que congelá-la corrigiu.
 */
export function jewelProcFromWire(wire: JewelProcSelectionWire | null): JewelProcSelection | null {
  return wire === null
    ? null
    : Object.freeze({ id: wire.id, entropy: wire.entropy, cutMilli: BigInt(wire.cutMilli) });
}

/**
 * O registo canónico: que joia origina que proc, e com que teto de chance.
 *
 * Uma tabela e não um `if`: a ordem por ID é o que torna os intervalos da
 * loteria reprodutíveis, e um registo é o sítio onde a segunda joia se liga sem
 * tocar no seletor.
 */
export interface OutgoingProcDefinition {
  readonly procId: OutgoingJewelProcId;
  readonly jewelId: string;
  readonly maxChanceBps: number;
}

/** O teto de chance do Ciclone, nomeado uma vez. */
export const CYCLONE_MAX_CHANCE_BPS = 1300;

/** O id da joia que origina o corte. Só o registo o conhece. */
export const CYCLONE_JEWEL_ID = BLADE_CUT_JEWEL_ID;

export const OUTGOING_PROC_REGISTRY: readonly OutgoingProcDefinition[] = Object.freeze([
  Object.freeze({
    procId: 'cyclone_cut' as const,
    jewelId: BLADE_CUT_JEWEL_ID,
    maxChanceBps: 1300,
  }),
]);

/**
 * Os rótulos dos fluxos de proc, estáveis e VERSIONADOS.
 *
 * Versionados porque mudar quantos valores um disparo consome muda toda a
 * sequência a partir daí. Um rótulo novo é a forma de declarar essa mudança em
 * vez de a deixar acontecer em silêncio numa sala já a decorrer.
 */
export const PVP_OUTGOING_PROC_RNG_LABEL = 'pvp-jewel-outgoing-proc-v1';
export const HUNT_OUTGOING_PROC_RNG_LABEL = 'hunt-jewel-outgoing-proc-v1';
/**
 * O gémeo do MUNDO ABERTO, com a mesma finalidade e a mesma unidade.
 *
 * Um rótulo próprio, e não uma reutilização: um rótulo é o que separa duas
 * sequências, e "de que modo é este fluxo" tem de continuar a ser legível numa
 * linha de import. A cadência e a razão de este fluxo ser separado dos outros
 * são as mesmas de sempre.
 */
export const OPEN_WORLD_OUTGOING_PROC_RNG_LABEL = 'open-world-jewel-outgoing-proc-v1';

/** O que a rolagem pode devolver quando nada foi selecionado. */
export const NO_PROC: null = null;

/**
 * O numerador de uma joia, dado o seu nível somado.
 *
 * Inteiro, e nunca dividido. Acima do teto devolve sempre o mesmo valor, que é
 * a chance a congelar.
 */
export function procChanceNumerator(maxChanceBps: number, levelSum: number): number {
  const effective = levelSum < PROC_LEVEL_CAP ? levelSum : PROC_LEVEL_CAP;
  return maxChanceBps * (effective < 0 ? 0 : effective);
}

/**
 * Os intervalos desta nave, por ordem canónica de ID.
 *
 * Cópias do mesmo ID já vêm agregadas no perfil - `levelSum` é `L × n` por
 * definição - portanto cada joia ocupa **um** intervalo, e não um por cópia.
 */
export interface ProcInterval {
  readonly procId: OutgoingJewelProcId;
  readonly from: number;
  readonly to: number;
  /** O teto de chance desta joia, para a magnitude sair da MESMA passagem. */
  readonly maxChanceBps: number;
  /** A soma de níveis que produziu esta largura. */
  readonly levelSum: bigint;
}

export function procIntervals(profile: JewelSocketProfile): readonly ProcInterval[] {
  const entries = [...OUTGOING_PROC_REGISTRY].sort((left, right) =>
    left.procId < right.procId ? -1 : 1,
  );
  const intervals: ProcInterval[] = [];
  let cursor = 0;
  for (const definition of entries) {
    const owned = profile.entries.find((entry) => entry.id === definition.jewelId);
    if (!owned) {
      continue;
    }
    const levelSum = toExactBigInt(owned.levelSum, `${owned.id} levelSum`);
    const width = procChanceNumerator(definition.maxChanceBps, Number(levelSum));
    if (width <= 0) {
      continue;
    }
    intervals.push({
      procId: definition.procId,
      from: cursor,
      to: cursor + width,
      maxChanceBps: definition.maxChanceBps,
      levelSum,
    });
    cursor += width;
  }
  if (cursor > PROC_CHANCE_DENOMINATOR) {
    /*
     * Recusa em vez de normalizar. Normalizar aqui esconderia um conjunto de
     * tetos que somam mais de cem por cento, que é uma decisão de equilíbrio
     * por fazer e não um caso a tratar em silêncio.
     */
    throw new RangeError(
      `proc intervals exceed the roll space: ${cursor} > ${PROC_CHANCE_DENOMINATOR}`,
    );
  }
  return intervals;
}

/**
 * **A seleção.** Uma rolagem, no máximo um proc.
 *
 * `roll` tem de vir de `[0, PROC_CHANCE_DENOMINATOR)` e ser produzido pelo fluxo
 * autoritativo do participante. `entropy` vem do mesmo sítio e é congelado com
 * a seleção.
 *
 * Devolve `null` quando a rolagem cai no espaço restante - o tiro comum - e
 * também quando a origem não pode originar proc nenhum. As duas razões são
 * diferentes e o chamador não precisa de as distinguir: nenhuma produz efeito.
 */
export function selectOutgoingProc(
  profile: JewelSocketProfile,
  origin: DamageOrigin,
  roll: number,
  entropy: number,
  baseMilli: bigint,
): JewelProcSelection | null {
  if (!damageOriginMayTriggerJewel(origin)) {
    return NO_PROC;
  }
  if (!Number.isInteger(roll) || roll < 0 || roll >= PROC_CHANCE_DENOMINATOR) {
    throw new RangeError(`proc roll outside [0, ${PROC_CHANCE_DENOMINATOR}): ${roll}`);
  }
  if (typeof baseMilli !== 'bigint' || baseMilli < 0n) {
    throw new RangeError(`a proc base is a non-negative bigint, received ${String(baseMilli)}`);
  }
  for (const interval of procIntervals(profile)) {
    /*
     * **A segunda metade da tabela fechada** (gate V28G.P).
     *
     * `damageOriginMayTriggerJewel` já disse que esta origem pode originar
     * ALGUMA coisa; isto diz se ela pode originar ESTA. É o que faz uma serra
     * poder cortar com o Ciclone e não poder lançar um míssil, sem que nenhuma
     * das duas simulações escreva a diferença como uma condição.
     *
     * Dentro do laço e não antes dele: uma origem futura pode autorizar um proc
     * do registo e recusar outro, e uma guarda à porta responderia por todos.
     * O intervalo continua a ser CONSUMIDO na mesma - `cursor` avança em
     * `procIntervals` - portanto recusar um proc não empurra a chance dos
     * seguintes para dentro do espaço dele.
     */
    if (!damageOriginMayTrigger(origin, interval.procId)) {
      continue;
    }
    if (roll >= interval.from && roll < interval.to) {
      /*
       * A MAGNITUDE é resolvida aqui, no nascimento, e congelada com a seleção.
       *
       * Este é o único sítio onde o resultado da loteria e o dano-base da nave
       * estão os dois na mão ao mesmo tempo. Devolver só o id obrigaria o
       * chamador a reencontrar a entrada do perfil no impacto - que é
       * precisamente a leitura tardia que este gate elimina, e a razão pela qual
       * as duas simulações deixam de precisar de conhecer uma joia pelo nome.
       */
      return Object.freeze({
        id: interval.procId,
        entropy: entropy >>> 0,
        cutMilli: procDamageMilli(baseMilli, interval.maxChanceBps, interval.levelSum),
      });
    }
  }
  return NO_PROC;
}

/**
 * O dano de um proc, em milli e exato.
 *
 *     dano = base × orçamentoPorNível × levelSum × cap
 *            ------------------------------------------
 *              maxChanceBps × min(levelSum, cap)
 *
 * Uma única divisão, no fim, e em `bigint` até lá. Até ao teto o numerador e o
 * denominador crescem os dois com `min(levelSum, cap)` e a magnitude fica
 * CONSTANTE. Acima dele **nada acontece**: os dois lados congelam juntos, e
 * `levelSum` 31, 180 ou um milhão devolvem exatamente o que 30 devolve.
 *
 * Congelavam-se em separado antes do V28G.K, e havia razão: com seis cópias de
 * nível 30 o denominador parava aos 30 e o numerador seguia até 180, de forma
 * que a chance ficava presa e a magnitude comprava o resto do orçamento - é daí
 * que vinham os 675 % do Núcleo Atômico. Uma skin passou a ter uma joia de cada
 * tipo, portanto `levelSum` É o nível e nunca passa dos 30; a segunda metade
 * dessa curva descreve uma configuração que deixou de poder existir.
 *
 * O clamp fica como DEFESA, e não como permissão: um perfil com 31 é recusado
 * na fronteira que o produziu - base, socket, API, bilhete, instantânea - e se
 * algum dia um chegar aqui, o que ele não vai é comprar poder.
 *
 * `baseMilli` é o dano-base AUTORITATIVO da nave e mais nada: sem
 * `jewel_damage`, sem Corações, sem runas, sem crítico e sem outro proc.
 */
export const PROC_BUDGET_BPS_PER_LEVEL = 30n;

export function procDamageMilli(
  baseMilli: bigint,
  maxChanceBps: number,
  levelSum: bigint,
  damageShareBps: bigint = BPS_ONE,
): bigint {
  if (levelSum <= 0n || damageShareBps <= 0n) {
    return 0n;
  }
  const effective = levelSum < BigInt(PROC_LEVEL_CAP) ? levelSum : BigInt(PROC_LEVEL_CAP);
  const numerator =
    baseMilli * PROC_BUDGET_BPS_PER_LEVEL * effective * BigInt(PROC_LEVEL_CAP) * damageShareBps;
  const denominator = BigInt(maxChanceBps) * effective * BPS_ONE;
  return numerator / denominator;
}

/** Uma joia que não origina proc nenhum não é procurada por engano. */
export const NON_PROC_JEWEL_IDS: readonly string[] = Object.freeze([ATTACK_SPEED_JEWEL_ID]);
